import { useState, useEffect, useMemo, useCallback } from "react";
import { axiosInstance } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ensureCsvPassword } from "@/lib/csvPassword";
import {
  Search, Users, Network, ChevronsRight, TrendingUp, FileDown, ChevronsUpDown, Check, X,
} from "lucide-react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

function formatDate(d) {
  if (!d) return "-";
  try { return new Date(d).toLocaleDateString("pt-BR"); } catch { return "-"; }
}

function formatCurrency(v) {
  const n = Number(v || 0);
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatKwh(v) {
  const n = Number(v || 0);
  return n.toLocaleString("pt-BR", { maximumFractionDigits: 2 }) + " kWh";
}

function formatMonth(monthStr) {
  if (!monthStr) return "";
  const [year, month] = monthStr.split("-");
  const months = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  return `${months[parseInt(month, 10) - 1] || month}/${year}`;
}

const SITUATION_MAP = {
  active: { label: "Ativo", emoji: "🟢", cls: "bg-green-100 text-green-700" },
  cancelled: { label: "Cancelado", emoji: "🔴", cls: "bg-red-100 text-red-700" },
  sent_for_signature: { label: "Enviado p/ Assinatura", emoji: "🔵", cls: "bg-blue-100 text-blue-700" },
  suspended: { label: "Suspenso", emoji: "🟠", cls: "bg-orange-100 text-orange-700" },
  excluded: { label: "Excluído", emoji: "⚫", cls: "bg-gray-100 text-gray-700" },
  missing_login: { label: "Falta de Login", emoji: "🟣", cls: "bg-purple-100 text-purple-700" },
  modality_change: { label: "Troca de Modalidade", emoji: "🔄", cls: "bg-cyan-100 text-cyan-700" },
  awaiting_ownership_transfer: { label: "Aguard. Troca Tit.", emoji: "🟦", cls: "bg-indigo-100 text-indigo-700" },
  ownership_transfer_requested: { label: "Troca Solicitada", emoji: "🟪", cls: "bg-fuchsia-100 text-fuchsia-700" },
  pending: { label: "Pendente", emoji: "🟡", cls: "bg-yellow-100 text-yellow-700" },
};

function situationInfo(s) {
  return SITUATION_MAP[s] || SITUATION_MAP.pending;
}

export default function ConsultaParceiro({ user, onLogout }) {
  const [allPartners, setAllPartners] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(true);
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);
  const [comboboxOpen, setComboboxOpen] = useState(false);

  const [sigStart, setSigStart] = useState("");
  const [sigEnd, setSigEnd] = useState("");

  const [subtree, setSubtree] = useState(null);
  const [loadingSubtree, setLoadingSubtree] = useState(false);
  const [cooperativesData, setCooperativesData] = useState(null);
  const [loadingCoops, setLoadingCoops] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  useEffect(() => {
    (async () => {
      try {
        setLoadingPartners(true);
        const res = await axiosInstance.get("/partners");
        const list = (res.data || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR"));
        setAllPartners(list);
      } catch (e) {
        toast.error("Erro ao carregar parceiros");
      } finally {
        setLoadingPartners(false);
      }
    })();
  }, []);

  const selectedPartner = useMemo(
    () => allPartners.find(p => p.id === selectedPartnerId) || null,
    [allPartners, selectedPartnerId]
  );

  const buildDateParams = useCallback(() => {
    const params = {};
    if (sigStart) params.signature_start = sigStart;
    if (sigEnd) params.signature_end = sigEnd;
    return params;
  }, [sigStart, sigEnd]);

  const runQuery = useCallback(async (partnerId, params) => {
    if (!partnerId) return;
    setLoadingSubtree(true);
    setLoadingCoops(true);
    const qs = new URLSearchParams(params).toString();
    const suffix = qs ? `?${qs}` : "";
    try {
      const [subRes, coopRes] = await Promise.all([
        axiosInstance.get(`/partners/${encodeURIComponent(partnerId)}/subtree${suffix}`),
        axiosInstance.get(`/partners/${encodeURIComponent(partnerId)}/cooperatives${suffix}`),
      ]);
      setSubtree(subRes.data);
      setCooperativesData(coopRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar dados do parceiro");
    } finally {
      setLoadingSubtree(false);
      setLoadingCoops(false);
    }
  }, []);

  // Auto-fetch when a partner is selected (with current date filter)
  useEffect(() => {
    if (selectedPartnerId) {
      runQuery(selectedPartnerId, buildDateParams());
    } else {
      setSubtree(null);
      setCooperativesData(null);
    }
  }, [selectedPartnerId, runQuery, buildDateParams]);

  const handleApplyFilter = () => {
    if (!selectedPartnerId) {
      toast.info("Selecione um parceiro primeiro.");
      return;
    }
    runQuery(selectedPartnerId, buildDateParams());
  };

  const handleClearFilters = () => {
    setSigStart("");
    setSigEnd("");
    if (selectedPartnerId) runQuery(selectedPartnerId, {});
  };

  const handleClearAll = () => {
    setSelectedPartnerId(null);
    setSigStart("");
    setSigEnd("");
    setSubtree(null);
    setCooperativesData(null);
  };

  const handleExportLinkedCsv = () => {
    if (!ensureCsvPassword()) return;
    if (!cooperativesData?.cooperatives?.length) {
      toast.info("Nenhum cooperado para exportar.");
      return;
    }
    const rows = cooperativesData.cooperatives.map(c => ({
      "Nome": c.name || "",
      "Distribuidora": c.distributor || "",
      "kWh Contratado": (c.contracted_kwh || 0).toString().replace('.', ','),
      "Data Assinatura": c.signature_date ? new Date(c.signature_date).toLocaleDateString("pt-BR") : "",
      "Status": situationInfo(c.situation).label,
      "Parceiro Direto": c.partner_name || "",
    }));
    const headers = Object.keys(rows[0]);
    const csvContent = "\ufeff" + headers.join(";") + "\n" +
      rows.map(r => headers.map(h => `"${(r[h] || "").toString().replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `cooperados_${(cooperativesData.root_name || "parceiro").replace(/\s+/g, "_")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const totals = useMemo(() => {
    if (!subtree?.partners) return null;
    return subtree.partners.reduce((acc, p) => {
      acc.cooperatives += p.cooperatives_count || 0;
      acc.kwh += p.total_kwh || 0;
      acc.received += p.commission_received || 0;
      acc.to_receive += p.commission_to_receive || 0;
      return acc;
    }, { cooperatives: 0, kwh: 0, received: 0, to_receive: 0 });
  }, [subtree]);

  // Aggregate linked cooperatives by situation for the status cards
  const statusBreakdown = useMemo(() => {
    const list = cooperativesData?.cooperatives || [];
    const map = {};
    for (const c of list) {
      const key = c.situation || "pending";
      if (!map[key]) map[key] = { count: 0, kwh: 0 };
      map[key].count += 1;
      map[key].kwh += Number(c.contracted_kwh || 0);
    }
    // Preferred display order
    const order = [
      "active", "pending", "sent_for_signature", "modality_change", "awaiting_ownership_transfer", "ownership_transfer_requested",
      "suspended", "missing_login", "cancelled", "excluded",
    ];
    return order
      .filter((k) => map[k])
      .map((k) => ({ situation: k, ...map[k], info: SITUATION_MAP[k] || SITUATION_MAP.pending }));
  }, [cooperativesData]);

  const hasDateFilter = sigStart || sigEnd;

  // Pagination for linked cooperatives
  useEffect(() => {
    setCurrentPage(1);
  }, [cooperativesData]);

  const totalPages = Math.max(
    1,
    Math.ceil((cooperativesData?.cooperatives?.length || 0) / itemsPerPage)
  );

  const paginatedCooperatives = useMemo(() => {
    const list = cooperativesData?.cooperatives || [];
    const start = (currentPage - 1) * itemsPerPage;
    return list.slice(start, start + itemsPerPage);
  }, [cooperativesData, currentPage, itemsPerPage]);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="consulta-parceiro-page">
        <div>
          <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2">Consulta Parceiro</h1>
          <p className="text-gray-600">
            Selecione um parceiro no dropdown e filtre os cooperados vinculados por data de assinatura.
          </p>
        </div>

        {/* Filters Card - always visible on top */}
        <Card className="border-0 shadow-lg" data-testid="filters-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Search className="w-5 h-5 text-blue-600" />
              Filtros
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
              {/* Partner combobox dropdown */}
              <div className="md:col-span-5">
                <Label className="mb-1 block">Parceiro</Label>
                <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={comboboxOpen}
                      className="w-full justify-between font-normal"
                      disabled={loadingPartners}
                      data-testid="partner-select-trigger"
                    >
                      <span className="truncate">
                        {selectedPartner
                          ? selectedPartner.name
                          : loadingPartners ? "Carregando parceiros..." : "Selecione um parceiro"}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command
                      filter={(value, search) => {
                        // value is the CommandItem `value` prop (name|email lowercased); return 1 if matches
                        return value.includes(search.toLowerCase()) ? 1 : 0;
                      }}
                    >
                      <CommandInput placeholder="Buscar por nome ou e-mail..." data-testid="partner-select-search" />
                      <CommandList>
                        <CommandEmpty>Nenhum parceiro encontrado.</CommandEmpty>
                        <CommandGroup>
                          {allPartners.map((p) => {
                            const value = `${(p.name || "").toLowerCase()}|${(p.email || "").toLowerCase()}`;
                            return (
                              <CommandItem
                                key={p.id}
                                value={value}
                                data-testid={`partner-option-${p.id}`}
                                onSelect={() => {
                                  setSelectedPartnerId(p.id);
                                  setComboboxOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedPartnerId === p.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div className="flex flex-col min-w-0">
                                  <span className="truncate font-medium">{p.name}</span>
                                  <span className="text-xs text-gray-500 truncate">
                                    {p.email || "sem e-mail"} • Nível {p.level ?? "-"} • {p.status === "active" ? "Ativo" : "Pendente"}
                                  </span>
                                </div>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Date filters */}
              <div className="md:col-span-3">
                <Label htmlFor="sig-start" className="mb-1 block">Data de Assinatura — Início</Label>
                <Input
                  id="sig-start"
                  type="date"
                  data-testid="signature-start-input"
                  value={sigStart}
                  onChange={(e) => setSigStart(e.target.value)}
                />
              </div>
              <div className="md:col-span-3">
                <Label htmlFor="sig-end" className="mb-1 block">Data de Assinatura — Fim</Label>
                <Input
                  id="sig-end"
                  type="date"
                  data-testid="signature-end-input"
                  value={sigEnd}
                  onChange={(e) => setSigEnd(e.target.value)}
                />
              </div>

              {/* Apply filter */}
              <div className="md:col-span-1 flex md:justify-end">
                <Button
                  data-testid="apply-filter-btn"
                  onClick={handleApplyFilter}
                  disabled={!selectedPartnerId || loadingSubtree || loadingCoops}
                  className="w-full md:w-auto bg-blue-600 hover:bg-blue-700"
                >
                  Aplicar
                </Button>
              </div>
            </div>

            {(selectedPartnerId || hasDateFilter) && (
              <div className="flex flex-wrap items-center gap-2 mt-4 text-xs text-gray-600">
                {selectedPartner && (
                  <Badge variant="secondary" data-testid="active-partner-badge">
                    Parceiro: <span className="font-semibold ml-1">{selectedPartner.name}</span>
                  </Badge>
                )}
                {sigStart && <Badge variant="outline">Início: {new Date(sigStart).toLocaleDateString("pt-BR")}</Badge>}
                {sigEnd && <Badge variant="outline">Fim: {new Date(sigEnd).toLocaleDateString("pt-BR")}</Badge>}
                {hasDateFilter && (
                  <button
                    type="button"
                    data-testid="clear-date-filter"
                    onClick={handleClearFilters}
                    className="text-blue-600 hover:underline inline-flex items-center gap-1"
                  >
                    <X className="w-3 h-3" /> limpar datas
                  </button>
                )}
                {selectedPartnerId && (
                  <button
                    type="button"
                    data-testid="clear-all-filters"
                    onClick={handleClearAll}
                    className="text-red-600 hover:underline inline-flex items-center gap-1"
                  >
                    <X className="w-3 h-3" /> limpar tudo
                  </button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {(loadingSubtree || loadingCoops) && (
          <div className="text-center py-12 text-gray-500" data-testid="loading-indicator">
            Carregando dados do parceiro...
          </div>
        )}

        {!loadingSubtree && !loadingCoops && !selectedPartnerId && (
          <Card className="border-0 shadow-lg">
            <CardContent className="py-10 text-center text-gray-500">
              Selecione um parceiro acima para visualizar cooperados vinculados e hierarquia.
            </CardContent>
          </Card>
        )}

        {/* Cooperados vinculados */}
        {!loadingCoops && cooperativesData && (
          <Card className="border-0 shadow-lg" data-testid="linked-cooperatives-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Cooperados vinculados a <span className="text-blue-700">{cooperativesData.root_name}</span>
              </CardTitle>
              <Button
                data-testid="export-linked-csv-btn"
                variant="outline"
                onClick={handleExportLinkedCsv}
                disabled={!cooperativesData?.cooperatives?.length}
              >
                <FileDown className="w-4 h-4 mr-2" />
                Exportar CSV
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="linked-status-cards">
                {/* Total card - always visible */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md" data-testid="linked-card-total">
                  <p className="text-xs text-gray-600">Total contratados</p>
                  <p className="text-2xl font-bold text-blue-700" data-testid="linked-count">{cooperativesData.count}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    <span data-testid="linked-total-kwh">{formatKwh(cooperativesData.total_kwh)}</span>
                  </p>
                </div>
                {/* Per-status cards */}
                {statusBreakdown.map((s) => (
                  <div
                    key={s.situation}
                    className={`p-3 rounded-md ${s.info.cls} border`}
                    data-testid={`linked-card-${s.situation}`}
                  >
                    <p className="text-xs opacity-80">{s.info.emoji} {s.info.label}</p>
                    <p className="text-2xl font-bold" data-testid={`linked-card-${s.situation}-count`}>{s.count}</p>
                    <p className="text-xs opacity-80 mt-1" data-testid={`linked-card-${s.situation}-kwh`}>
                      {formatKwh(s.kwh)}
                    </p>
                  </div>
                ))}
              </div>

              {cooperativesData.cooperatives.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Nenhum cooperado encontrado para o filtro aplicado.</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table data-testid="linked-cooperatives-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome</TableHead>
                          <TableHead>Distribuidora</TableHead>
                          <TableHead className="text-right">kWh</TableHead>
                          <TableHead>Data Assinatura</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Parceiro Direto</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedCooperatives.map(c => {
                          const st = situationInfo(c.situation);
                          return (
                            <TableRow key={c.id} data-testid={`linked-coop-row-${c.id}`}>
                              <TableCell className="font-medium">{c.name || "-"}</TableCell>
                              <TableCell>{c.distributor}</TableCell>
                              <TableCell className="text-right">{formatKwh(c.contracted_kwh)}</TableCell>
                              <TableCell>{formatDate(c.signature_date)}</TableCell>
                              <TableCell>
                                <span
                                  data-testid={`linked-coop-status-${c.id}`}
                                  className={`px-2 py-1 rounded-full text-xs font-semibold ${st.cls}`}
                                >
                                  {st.emoji} {st.label}
                                </span>
                              </TableCell>
                              <TableCell className="text-sm">{c.partner_name || "-"}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination controls */}
                  <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t" data-testid="linked-pagination">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Itens por página:</span>
                      <Select
                        value={itemsPerPage.toString()}
                        onValueChange={(v) => { setItemsPerPage(Number(v)); setCurrentPage(1); }}
                      >
                        <SelectTrigger className="w-20" data-testid="linked-items-per-page-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="text-sm text-gray-500">
                        Mostrando {Math.min((currentPage - 1) * itemsPerPage + 1, cooperativesData.cooperatives.length)} - {Math.min(currentPage * itemsPerPage, cooperativesData.cooperatives.length)} de {cooperativesData.cooperatives.length} cooperados
                      </span>
                    </div>
                    {cooperativesData.cooperatives.length > itemsPerPage && (
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          variant="outline"
                          size="sm"
                          data-testid="linked-page-prev"
                        >
                          Anterior
                        </Button>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                            .map((page, idx, arr) => {
                              const prev = arr[idx - 1];
                              const showEllipsis = prev && page - prev > 1;
                              return (
                                <div key={page} className="flex items-center">
                                  {showEllipsis && <span className="px-2 text-gray-400">...</span>}
                                  <Button
                                    onClick={() => setCurrentPage(page)}
                                    variant={currentPage === page ? "default" : "outline"}
                                    size="sm"
                                    className={currentPage === page ? "bg-blue-600" : ""}
                                    data-testid={`linked-page-${page}`}
                                  >
                                    {page}
                                  </Button>
                                </div>
                              );
                            })}
                        </div>
                        <Button
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage >= totalPages}
                          variant="outline"
                          size="sm"
                          data-testid="linked-page-next"
                        >
                          Próximo
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Hierarchy Card */}
        {!loadingSubtree && subtree && (
          <>
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Network className="w-5 h-5 text-blue-600" />
                  Hierarquia de <span className="text-blue-700">{subtree.root_name}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="p-3 bg-blue-50 rounded-md">
                    <p className="text-xs text-gray-600">Parceiros (inclui raiz)</p>
                    <p className="text-2xl font-bold text-blue-700" data-testid="total-partners">{subtree.count}</p>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-md">
                    <p className="text-xs text-gray-600">Cooperados Ativos</p>
                    <p className="text-2xl font-bold text-emerald-700" data-testid="total-cooperatives">{totals?.cooperatives || 0}</p>
                  </div>
                  <div className="p-3 bg-cyan-50 rounded-md">
                    <p className="text-xs text-gray-600">kWh Contratado</p>
                    <p className="text-lg font-bold text-cyan-700" data-testid="total-kwh">{formatKwh(totals?.kwh)}</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-md">
                    <p className="text-xs text-gray-600">Comissão Recebida</p>
                    <p className="text-lg font-bold text-green-700" data-testid="total-received">{formatCurrency(totals?.received)}</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-md">
                    <p className="text-xs text-gray-600">Comissão a Receber</p>
                    <p className="text-lg font-bold text-amber-700" data-testid="total-to-receive">{formatCurrency(totals?.to_receive)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  Parceiros na hierarquia ({subtree.count})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {subtree.partners.length === 0 ? (
                  <p className="text-center text-gray-500 py-6">Nenhum parceiro encontrado.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table data-testid="subtree-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Hierarquia</TableHead>
                          <TableHead>Nome</TableHead>
                          <TableHead>E-mail</TableHead>
                          <TableHead>Telefone</TableHead>
                          <TableHead>CPF/CNPJ</TableHead>
                          <TableHead>Parceiro Pai</TableHead>
                          <TableHead>Nível</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-center">Cooperados</TableHead>
                          <TableHead className="text-right">kWh</TableHead>
                          <TableHead className="text-right">Comissão Recebida</TableHead>
                          <TableHead className="text-right">Comissão a Receber</TableHead>
                          <TableHead>Cadastrado em</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {subtree.partners.map((p) => {
                          const indent = (p.relative_level || 0) * 16;
                          const isRoot = p.id === subtree.root_id;
                          return (
                            <TableRow key={p.id} data-testid={`partner-row-${p.id}`} className={isRoot ? "bg-blue-50/50" : ""}>
                              <TableCell>
                                <div className="flex items-center" style={{ paddingLeft: `${indent}px` }}>
                                  {p.relative_level > 0 && <ChevronsRight className="w-3 h-3 text-gray-400 mr-1" />}
                                  <span className="text-xs text-gray-500">
                                    {isRoot ? "Raiz" : `Nível +${p.relative_level}`}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="font-medium">{p.name}</TableCell>
                              <TableCell className="text-sm">{p.email || "-"}</TableCell>
                              <TableCell className="text-sm">{p.phone || "-"}</TableCell>
                              <TableCell className="text-sm">{p.cpf_cnpj || "-"}</TableCell>
                              <TableCell className="text-sm">{p.parent_name || "-"}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{p.level ?? "-"}</Badge>
                              </TableCell>
                              <TableCell>
                                <Badge className={p.status === "active" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                                  {p.status === "active" ? "Ativo" : "Pendente"}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center font-medium">{p.cooperatives_count}</TableCell>
                              <TableCell className="text-right">{formatKwh(p.total_kwh)}</TableCell>
                              <TableCell className="text-right text-green-700">{formatCurrency(p.commission_received)}</TableCell>
                              <TableCell className="text-right text-amber-700">{formatCurrency(p.commission_to_receive)}</TableCell>
                              <TableCell className="text-sm">{formatDate(p.created_at)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg" data-testid="kwh-evolution-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  Evolução Mensal de kWh Contratado
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!subtree.kwh_evolution || subtree.kwh_evolution.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Sem dados de assinatura para a hierarquia selecionada.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={380}>
                    <ComposedChart data={subtree.kwh_evolution} margin={{ top: 20, right: 30, left: 10, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="month"
                        tickFormatter={formatMonth}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis
                        yAxisId="left"
                        tickFormatter={(v) => v.toLocaleString("pt-BR")}
                        label={{ value: "kWh do mês", angle: -90, position: "insideLeft" }}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tickFormatter={(v) => v.toLocaleString("pt-BR")}
                        label={{ value: "kWh acumulado", angle: 90, position: "insideRight" }}
                      />
                      <Tooltip
                        formatter={(value, name) => [Number(value).toLocaleString("pt-BR", { maximumFractionDigits: 2 }) + " kWh", name]}
                        labelFormatter={formatMonth}
                      />
                      <Legend />
                      <Bar yAxisId="left" dataKey="kwh" fill="#3b82f6" name="kWh contratado no mês" />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="cumulative_kwh"
                        stroke="#10b981"
                        strokeWidth={3}
                        dot={{ r: 4 }}
                        name="kWh acumulado"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
